import { Component, Input, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import {
  calendarInterval,
  CalendarSchedulingEvent,
  calendarStatus
} from '@services/dws/scheduling/calendar-event/calendar-scheduling-event';
import { capitalizeEachWord } from '@util/transform-string/capitalize-each-word';
import { TranslateService } from '@ngx-translate/core';
import { formatLabelsFromEvent } from '../../../../../util/calendar-reservation';
import { CurrencyPipe, getCurrencySymbol } from '@angular/common';
import { VwReservation } from '@interfaces/dws/reservation';
import * as moment from 'moment';
import { Room } from '@interfaces/dws/room';
import { Employee } from '@interfaces/dws/employee';
import { locale } from '@util/locale';
import { tr } from '@util/tr';

const WAITING_STATE_STRING = 'waiting';
const COMPLETED_STATE_STRING = 'completed';
const CONFIRMED_STATE_STRING = 'confirmed';
const REJECTED_STATE_STRING = 'rejected';
const REVOKED_STATE_STRING = 'revoked';
const CANCELED_STATE_STRING = 'canceled';

export type EventType = 'e30min' | 'e1hr' | 'grouped' | 'appointment' | 'groupede30min';

@Component({
  selector: 'app-reservation-calendar-event',
  templateUrl: './reservation-calendar-event.component.html',
  styleUrls: ['./reservation-calendar-event.component.scss']
})
export class ReservationCalendarEventComponent implements OnInit, AfterViewInit {
  private _meta: VwReservation = {} as any;
  public capitalizeEachWord = capitalizeEachWord;
  language = locale().locale;
  public formatLabelsFromEvent = formatLabelsFromEvent;
  cPipe: CurrencyPipe;
  public moment = moment;
  @Input() smallEvent: boolean = false;

  @Input() rooms: Room[] = [];
  @Input() employees: Employee[] = [];

  // Cached values to prevent expression changed errors
  private _cachedContactFullName: string = '';
  private _cachedCustomerTooltip: string | null = null;
  private _cachedExperienceTooltip: string | null = null;

  constructor(public translate: TranslateService, private cpipe: CurrencyPipe) {
    this.cPipe = cpipe;
  }

  @Input() set meta(v: VwReservation) {
    this.status = calendarStatus(v);
    this._meta = v;
    // Clear cached values when meta changes
    this._cachedContactFullName = '';
    this._cachedCustomerTooltip = null;
    this._cachedExperienceTooltip = null;
  }

  get meta() { return this._meta; }

  status: { cssClass: string; icon: string; text: string; } = {} as any;

  private uiLanguage(): string {
    return this.translate.currentLang || this.translate.getDefaultLang() || 'en';
  }

  private pickTranslation(
    translations: Record<string, string> | undefined | null,
    preferredLang: string
  ): string {
    if (!translations || typeof translations !== 'object') {
      return '';
    }
    const direct = translations[preferredLang];
    if (direct?.trim()) {
      return direct;
    }
    const en = translations['en'];
    if (en?.trim()) {
      return en;
    }
    const it = translations['it'];
    if (it?.trim()) {
      return it;
    }
    const first = Object.values(translations).find((v) => !!v?.trim?.());
    return first || '';
  }

  get currencySymbol() {
    return getCurrencySymbol(this._meta?.paymentCurrency || 'EUR', 'wide', undefined) || '' as string;
  }

  formatTotal(totalCents: number): string {
    let money = (totalCents / 100);
    return this.cPipe.transform(money, this.currencySymbol, "symbol", "1.0-0")!;
  }

  countInWaiting(childs: CalendarSchedulingEvent[]): number {
    let count = 0;
    if (childs && childs.length > 0) {
      let thisStateElems = childs.filter(x => x.state === WAITING_STATE_STRING);
      return thisStateElems.length;
    }
    return count;
  }

  inWaitingClass(): string {
    let childs = this.meta.optionalData?.childEvents;
    if (childs && childs.length > 0) {
      return "waitingGroup";
    }
    return "";
  }

  @Input() width!: number;
  @Input() height!: number;
  @Input() view: 'week' | 'day' | 'month' = 'week';

  // New input to specify event type, or auto-detect
  @Input() eventType?: EventType;

  ngOnInit() {
    if (!this.eventType) {
      this.eventType = this.detectEventType();
    }
  }

  ngAfterViewInit() {
    // View is initialized, tooltips will work now
    // Cache tooltip values after view is initialized
    this.updateCachedValues();
  }

  interval(): number {
    return calendarInterval(this.meta as CalendarSchedulingEvent)
  }

  get contactFullName() {
    if (this._cachedContactFullName) {
      return this._cachedContactFullName;
    }
    
    let contact = this.meta.reservationContacts?.find(contact => contact.contact_type === 'MASTER');
    if (!contact) {
      this._cachedContactFullName = '';
      return '';
    }
    if (!contact.first_name && !contact.last_name) {
      this._cachedContactFullName = "No name";
      return "No name";
    }
    this._cachedContactFullName = `${contact.first_name || ''} ${contact.last_name || ''}`;
    return this._cachedContactFullName;
  }

  roomName(roomId: string): string {
    return this.rooms.find(x => x.id === roomId)?.name || '';
  }

  employeeName(employeeId: string): string {
    if (this.eventType === 'grouped') {
      // For grouped events, handle multiple employee IDs
      const ids = employeeId.split(', ');
      const employees = this.employees.filter(e => ids.includes(e.id) && (e?.firstName || e?.lastName));
      if (employees.length === 0) return '';
      return (employees[0].firstName || '') + ' ' + (employees[0].lastName || '') + (employees.length > 1 ? ' + ' + (employees.length - 1) : '');
    } else {
      // For single events, handle single employee ID
      const employee = this.employees.find(x => x.id === employeeId);
      if (!employee) return '';
      return (employee?.firstName + ' ' + employee?.lastName) || '';
    }
  }

  getExperienceName(reservation: VwReservation): string {
    const translations = reservation.experienceTranslations?.find(
      (t) => t.translation_id === 'TITLE'
    )?._translations;
    return this.pickTranslation(translations, this.uiLanguage());
  }

  countGuests(meta: VwReservation): number {
    var simple = meta.guestCount01 || 0;
    if (meta.reservationPriceLabels?.length || -1 > 0) {
      meta.reservationPriceLabels!.forEach(x => simple += x.quantity);
    }
    return simple;
  }

  getEventTitle(): string {
    if (this.eventType === 'appointment') {
      return this.meta.optionalData?.appointment_title || '-';
    } else {
      return this.getExperienceName(this.meta);
    }
  }

  localizedLanguageLabel(): string {
    const iso = this.meta.languageIso;
    if (!iso) {
      return '';
    }
    return iso;
  }

  private detectEventType(): EventType {
    // Check if it's an appointment
    if (this.meta.optionalData?.appointment_title) {
      return 'appointment';
    }
    
    // Check if it's a grouped event (has child events)
    if (this.meta.optionalData?.childEvents && this.meta.optionalData.childEvents.length > 0) {
      if (this.interval() < 3600) { // 30 minutes in seconds
        return 'groupede30min';
      } else {
        return 'grouped';
      }
    }
    
    // Check if it's a 30-minute event (short duration)
    if (this.interval() < 3600) { // 30 minutes in seconds
      return 'e30min';
    }
    
    // Default to 1hr+ for longer events
    return 'e1hr';
  }

  getCardClasses(): string {
    let classes = this.eventType || '';
    
    if (this.eventType === 'grouped' || this.eventType === 'groupede30min') {
      classes += ' multi';
    } else if (this.eventType === 'appointment') {
      classes += ' appointment';
    } else {
      classes += ' ' + (this.status.cssClass || '');
    }
    
    if (this.meta.isTourOperator) {
      classes += ' tour-operator';
    }
    
    return classes;
  }

  getStatusIcon(): string {
    if (this.eventType === 'grouped' || this.eventType === 'groupede30min') {
      return 'alarm'; // Changed from 'schedule' to 'alarm' for multi
    } else if (this.eventType === 'appointment') {
      return 'event'; // Calendar icon for appointment
    }
    
    switch (this.status.cssClass) {
      case 'waiting':
        return 'schedule';
      case 'confirmed':
        return 'check_circle';
      case 'completed':
        return 'check_circle';
      case 'draft':
        return 'edit';
      case 'rejected':
      case 'revoked':
      case 'canceled':
        return 'cancel';
      default:
        return 'event';
    }
  }

  getStatusText(): string {
    if (this.eventType === 'grouped' || this.eventType === 'groupede30min') {
      return tr('Multi');
    } else if (this.eventType === 'appointment') {
      return tr('Appointment');
    } else {
      return this.status.text;
    }
  }

  getCardStyles(): any {
    const styles: any = {};
    
    // Use experience color for border-left if available
    if (this.meta.experience && this.meta.experience[0]?.color) {
      styles['border-left-color'] = this.meta.experience[0].color;
    }
    
    return styles;
  }

  // Method to check if text is truncated (needs tooltip)
  isTextTruncated(element: HTMLElement): boolean {
    if (!element) return false;
    return element.scrollWidth > element.clientWidth;
  }

  // Get tooltip text only if text is truncated
  getTooltipText(text: string, elementRef?: HTMLElement): string | null {
    if (!text || !elementRef) return null;
    return this.isTextTruncated(elementRef) ? text : null;
  }

  // Update cached tooltip values
  private updateCachedValues(): void {
    // This will be called after view init to cache tooltip values
    // The actual tooltip text will be determined by the template
  }

  // Get cached customer tooltip text
  getCustomerTooltipText(): string | null {
    if (this._cachedCustomerTooltip !== null) {
      return this._cachedCustomerTooltip;
    }
    
    if (this.eventType === 'grouped' || this.eventType === 'groupede30min') {
      this._cachedCustomerTooltip = (this.countGuests(this.meta) + ' ' + this.translate.instant('Participants'));
    } else {
      this._cachedCustomerTooltip = this.capitalizeEachWord(this.contactFullName);
    }
    
    return this._cachedCustomerTooltip;
  }

  // Get cached experience tooltip text
  getExperienceTooltipText(): string | null {
    if (this._cachedExperienceTooltip !== null) {
      return this._cachedExperienceTooltip;
    }
    
    this._cachedExperienceTooltip = this.getEventTitle();
    return this._cachedExperienceTooltip;
  }

}
